import { useQueryClient } from '@tanstack/react-query';
import { useCart } from 'hooks/cart/useCart';
import { useDiscounts } from 'hooks/cart/useDiscounts';
import { useShopifyProducts } from 'hooks/product/useShopifyProducts';
import { useAnalytics } from 'hooks/useAnalytics';
import { useEffect, useMemo, useRef } from 'react';

import {
  getProgress,
  giftAlreadyInCart,
  hasAnyWhitelistedProduct,
  isPromoCodeActive,
  normalizePromoCode,
} from './utils/cartPromoHelpers';

const PROMO_WHITELIST = [
  'gentle-cleanser',
  'gentle-cleanser-mini',
  'resurfacing-exfoliator',
  'resurfacing-exfoliator-mini',
  'eye-cream',
  'firming-serum',
  'firming-serum-refill',
  'firming-serum-mini',
  'barrier-cream',
  'barrier-cream-refill',
  'barrier-cream-mini',
  'dewy-cream',
  'dewy-cream-refill',
  'dewy-cream-mini',
  'skin-longevity-essentials',
];
const PROMO_THRESHOLD = 300; // store currency
const PROMO_GIFT_PRODUCTS: string[] = ['firming-serum-mini-gift'];
const PROMO_GIFT_CODES: string[] = [];

export interface PromoProduct {
  slug: string;
  variant: { gid: string };
}

interface UseCartPromosResult {
  shouldShowOffer: boolean;
  disabled: boolean;
  remainingAmount: number;
  promoProducts: PromoProduct[];
  progress: number;
}

export const useCartPromos = (): UseCartPromosResult => {
  const queryClient = useQueryClient();
  const { data: cart } = useCart();
  const shopifyProducts = useShopifyProducts({ handles: PROMO_GIFT_PRODUCTS });
  const { addDiscounts } = useDiscounts(queryClient);
  const { analytics } = useAnalytics();
  const loggedAcceptsRef = useRef<Set<string>>(new Set());
  const loggedShowRef = useRef<boolean>(false);

  const promoProducts = useMemo(
    () =>
      shopifyProducts
        .map((product, index) => {
          const variant =
            product.variants.find((v) => v.availableForSale) ||
            product.variants[0];

          return {
            slug: PROMO_GIFT_PRODUCTS[index],
            variant: { gid: variant.id },
            availableForSale: variant.availableForSale,
          };
        })
        .filter((p) => p.availableForSale),
    [shopifyProducts],
  );

  const { shouldShowOffer, disabled, remainingAmount, progress } =
    useMemo(() => {
      if (!hasAnyWhitelistedProduct(cart, PROMO_WHITELIST))
        return {
          shouldShowOffer: false,
          disabled: true,
          remainingAmount: PROMO_THRESHOLD,
          progress: 0,
        } as UseCartPromosResult;

      const { progress: nextProgress, remainingAmount: nextRemainingAmount } =
        getProgress(cart, PROMO_THRESHOLD, PROMO_WHITELIST);

      if (isPromoCodeActive(cart, PROMO_GIFT_CODES))
        return {
          shouldShowOffer: false,
          disabled: true,
          remainingAmount: nextRemainingAmount,
          progress: nextProgress,
        } as UseCartPromosResult;

      if (giftAlreadyInCart(cart, PROMO_GIFT_PRODUCTS)) {
        return {
          shouldShowOffer: false,
          disabled: true,
          remainingAmount: nextRemainingAmount,
          progress: nextProgress,
        } as UseCartPromosResult;
      }

      return {
        shouldShowOffer: promoProducts.length > 0,
        disabled: nextRemainingAmount > 0,
        remainingAmount: nextRemainingAmount,
        progress: nextProgress,
      } as UseCartPromosResult;
    }, [cart, promoProducts.length]);

  const shouldApplyCode = useMemo(() => {
    if (!cart) return false;
    return (
      giftAlreadyInCart(cart, PROMO_GIFT_PRODUCTS) &&
      !isPromoCodeActive(cart, PROMO_GIFT_CODES)
    );
  }, [cart]);

  useEffect(() => {
    if (!shouldApplyCode) return;
    addDiscounts(PROMO_GIFT_CODES.map(normalizePromoCode));
    // only when transitions into state requiring code
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldApplyCode]);

  useEffect(() => {
    if (!shouldShowOffer) return;
    if (!loggedShowRef.current) {
      analytics.freegiftShowProgressBar();
      promoProducts.forEach((p) => {
        analytics.freegiftShowChoices({ product: p.slug });
      });
      loggedShowRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowOffer, promoProducts]);

  // Fire analytics: accept when any gift handle appears in cart (once per handle)
  useEffect(() => {
    if (!cart) return;
    const giftsInCart = cart.lines
      .map((l) => l.merchandise.product.handle)
      .filter((h) => PROMO_GIFT_PRODUCTS.includes(h));

    for (const handle of giftsInCart) {
      if (!loggedAcceptsRef.current.has(handle)) {
        analytics.freegiftAccept({ product: handle });
        loggedAcceptsRef.current.add(handle);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  return {
    shouldShowOffer,
    disabled,
    remainingAmount,
    promoProducts,
    progress,
  };
};
